// ProtectedRoute — v0.7.0 (Auth + Saved Analyses)
//
// Route guard. Wrap any element that requires a signed-in user:
//   <Route path="/deal-analyzer" element={
//     <ProtectedRoute><DealAnalyzer /></ProtectedRoute>
//   } />
//
// While the session is still hydrating we show a light placeholder so the
// page doesn't flash to /login and back. Unauthenticated users are sent to
// /login with the attempted path stashed in location state, so Login can
// send them back where they were headed.

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { hasAcceptedCurrentLegal } from '../lib/legalVersions';
import AcceptTerms from './AcceptTerms';

const centered = {
  minHeight: '60vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#6b7280',
  fontSize: '18px',
};

export default function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  // PLATFORM-002 (ADR-058) — clickwrap gate. The subscription context already
  // loads the profiles row (which carries the acceptance columns); this is the
  // single enforcement point for every protected route, so it covers
  // email/password, OAuth, invite onboarding, existing users, and re-acceptance
  // after a version bump uniformly. useSubscription() may be null in isolated
  // test renders with no provider — in that case the gate is skipped (in
  // production the provider always wraps the app).
  const subscription = useSubscription();

  if (loading) {
    return <div style={centered}>Loading your workspace…</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (subscription) {
    // Wait for the profile to load before deciding, so we don't flash the gate.
    if (subscription.loading) {
      return <div style={centered}>Loading your workspace…</div>;
    }
    if (!hasAcceptedCurrentLegal(subscription.profile)) {
      return <AcceptTerms onAccepted={subscription.refreshProfile} />;
    }
  }

  return children;
}
